/**
 * Insurance Third Party Authorization Form (Jaron, Oct 29 2026 PDF, ticket
 * 13411499936). Table rows in the form's order, then "(Company Name)" inside
 * the authorization sentence. The Oct 28 version's Insurance Company, Contact
 * Person, Phone/Email and Date lines are gone from the form.
 */
import { roofClaimLocked } from './roofClaimGate'

export const ROOF_AUTH_FIELDS = {
  policyholder: 'roof_third_party_policyholder',
  address: 'roof_third_party_address',
  claimNumber: 'roof_third_party_claim_number',
  policyNumber: 'roof_third_party_policy_number',
  company: 'roof_third_party_company',
} as const
export type RoofAuthField =
  (typeof ROOF_AUTH_FIELDS)[keyof typeof ROOF_AUTH_FIELDS]
export interface RoofAuthDefaults {
  /** Same contractor name the Roof Claim agreement prints. */
  company: string
}
const text = (v: unknown): string => (typeof v === 'string' ? v : '')
export const authorizationSigned = (
  customer: Readonly<Record<string, unknown>>,
): boolean => !!text(customer['roof_third_party_sign']).trim()

/**
 * Jaron, Nov 4: "where do we put in the policy number? That needs to be
 * required". Same rule in person as by email (REMOTE_SEND_RULES): no signature
 * without it. An authorization already signed keeps what it has (V67).
 */
export const authorizationNeedsPolicyNumber = (
  customer: Readonly<Record<string, unknown>>,
): boolean =>
  !authorizationSigned(customer) &&
  !text(customer[ROOF_AUTH_FIELDS.policyNumber]).trim()

/** The rep can type it; `readOnly` is the customer's own link, who cannot. */
export const authorizationPolicyNumberMessage = (readOnly: boolean): string =>
  readOnly
    ? 'Policy Number is required. Ask your representative to fill it in on this Insurance Third Party Authorization Form before you sign'
    : 'Policy Number is required. Type it in the Policy Number box of this Insurance Third Party Authorization Form, then sign'

/**
 * When the authorization became required at Finish in person. A Roof Claim
 * signed before this went through without it, so it keeps its Finish as it was
 * (V67). A signature with no usable date counts as old.
 */
export const AUTHORIZATION_REQUIRED_SINCE = Date.UTC(2026, 10, 4, 21, 0)

/**
 * Off: Jaron asked for the Policy Number to be required, not for this form to
 * block Finish in person. Open question on 13411499936; flip when he decides.
 */
export const AUTHORIZATION_REQUIRED_IN_PERSON = false

export const authorizationRequiredInPerson = (
  customer: Readonly<Record<string, unknown>>,
): boolean =>
  !['roof_claiming_client_sign', 'roof_claiming_company_rep_sign'].some(
    (field) => {
      if (!text(customer[field]).trim()) return false
      const date = customer[field + '_date']
      return !(typeof date === 'number' && date >= AUTHORIZATION_REQUIRED_SINCE)
    },
  )
/**
 * Unsigned: a typed value wins, otherwise the claim data is shown (never
 * persisted on mount). The sanitizer turns a missing field into '', so empty
 * means "not typed" unless the rep cleared it in this session (`cleared`).
 * Signed: the snapshot taken at signing, blanks included, nothing else.
 */
export function roofAuthorizationValues(
  customer: Readonly<Record<string, unknown>>,
  defaults: RoofAuthDefaults,
  cleared: ReadonlySet<string> = new Set(),
): Record<RoofAuthField, string> {
  const frozen = authorizationSigned(customer)
  const fallback: Record<keyof typeof ROOF_AUTH_FIELDS, string> = {
    policyholder:
      text(customer['roof_claiming_homeowner']) ||
      text(customer['customerName']),
    address:
      [
        customer['roof_claiming_address'],
        customer['roof_claiming_city'],
        customer['roof_claiming_zip'],
      ]
        .map(text)
        .filter(Boolean)
        // Only while the claim is unsigned: a signed packet looks as it did
        // (V67). A new claim's first signature stores the address above.
        .join(', ') ||
      (roofClaimLocked(customer)
        ? ''
        : text(customer['customerAddress']).trim()),
    claimNumber: text(customer['roof_claiming_claim']),
    policyNumber: '',
    company: defaults.company,
  }
  return Object.fromEntries(
    Object.entries(ROOF_AUTH_FIELDS).map(([key, field]) => {
      const stored = text(customer[field])
      if (frozen || stored || cleared.has(field)) return [field, stored]
      return [field, fallback[key as keyof typeof ROOF_AUTH_FIELDS]]
    }),
  ) as Record<RoofAuthField, string>
}
/** Fresh drawing only. Snapshot visible form and date atomically in its own namespace. */
export function roofAuthorizationSignaturePatch(
  customer: Readonly<Record<string, unknown>>,
  signature: string,
  now: number,
  defaults: RoofAuthDefaults,
  cleared: ReadonlySet<string> = new Set(),
) {
  if (
    authorizationSigned(customer) ||
    authorizationNeedsPolicyNumber(customer) ||
    !signature.trim() ||
    !Number.isFinite(now) ||
    now <= 0
  )
    return null
  return {
    ...roofAuthorizationValues(customer, defaults, cleared),
    roof_third_party_sign: signature,
    roof_third_party_sign_date: now,
  }
}
